import React from 'react';
import { FaStar, FaStarHalf } from 'react-icons/fa';

interface RatingProps {
  /** The actual value */
  value: number;

  /** An optional label to describe the rating */
  label?: string;
}

/** 
 * Returns an array with 5 stars which are filled (empty / half / full) based 
 * on the given `value`.
 */
function getStars(value: number) {
  const normalizedValue = Math.round((value * 5) / 0.5) * 0.5;

  return new Array(5).fill(null).map((_, i) => {
    if (normalizedValue <= i) {
      return <FaStar className="text-gray-200" />;
    }

    if (normalizedValue - i === 0.5) {
      return (
        <>
          <FaStarHalf className="text-teal-500 relative z-10" />
          <FaStar className="text-gray-200 absolute top-0 z-0" />
        </>
      );
    }

    return <FaStar className="text-teal-500" />;
  });
}

/** Renders a (5) star rating based on the given value. */
export default function Rating({ label, value }: RatingProps) {
  const stars = getStars(value);

  return (
    <figure className="flex flex-col gap-2 items-center" title={value.toFixed(2)}>
      <div className="flex gap-2">
        {stars.map((star, i) => (
          <div className="relative" key={`star-${i}`}>
            {star}
          </div>
        ))}
      </div>
      {label && <figcaption className="text-center">{label}</figcaption>}
    </figure>
  );
}
